import React, { useEffect, useState } from "react";
import { Trash2, Check, X, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDateTime } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal, Badge, StatCard, PhotoUpload } from "@/components/common";

const REASONS = [["layu", "Layu/kering"], ["rusak", "Rusak/patah"], ["rontok", "Rontok"], ["expired", "Expired"], ["busuk", "Busuk"], ["salah_potong", "Salah potong"], ["hilang", "Hilang"], ["lainnya", "Lainnya"]];

export default function Waste() {
  const { branch, can, isOwner } = useAuth();
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const canApprove = can("waste", "approve") || isOwner;

  const load = async () => {
    setLoading(true);
    try {
      const p = branch ? { branch_id: branch.id } : {};
      const [pend, hist, sum] = await Promise.all([
        api.get("/inventory/waste/requests", { ...p, status: "pending" }),
        api.get("/inventory/waste", p),
        api.get("/inventory/waste/summary", p).catch(() => null),
      ]);
      setPending(pend); setHistory(hist); setSummary(sum);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branch]);

  const decide = async (id, action) => {
    let notes = "";
    if (action === "reject") { notes = window.prompt("Alasan penolakan?") || ""; if (!notes) return; }
    try { await api.post(`/inventory/waste/requests/${id}/${action}`, { notes, force: action === "approve" }); toast.success(action === "approve" ? "Disetujui & stok dipotong" : "Ditolak"); load(); }
    catch (e) {
      if (e.status === 409 && action === "approve" && window.confirm(e.message + "\n\nPotong sebatas stok tersedia?")) {
        try { await api.post(`/inventory/waste/requests/${id}/approve`, { notes, force: true }); toast.success("Disetujui (dipotong sebatas stok)"); load(); } catch (e2) { toast.error(e2.message); }
      } else toast.error(e.message);
    }
  };

  return (
    <>
      <PageHeader title="Bunga Rusak / Waste" subtitle={branch?.name} icon={Trash2}
        actions={can("waste", "create") && <Button onClick={() => setCreating(true)} data-testid="add-waste-btn"><Plus size={16} /> Lapor Rusak</Button>} />

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard label="Waste Hari Ini" value={rupiah(summary.today?.value || 0)} sub={`${num(summary.today?.count || 0)} kejadian`} tone="warning" />
          <StatCard label="Waste Bulan Ini" value={rupiah(summary.month?.value || 0)} tone="danger" />
          <StatCard label="Menunggu Approval" value={num(pending.length)} tone="warning" />
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {[["pending", "Menunggu Approval"], ["history", "Riwayat"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-4 h-9 rounded-xl text-sm font-medium ${tab === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}{v === "pending" && pending.length > 0 ? ` (${pending.length})` : ""}</button>
        ))}
      </div>

      {loading ? <Loading /> : tab === "pending" ? (
        !pending.length ? <EmptyState icon={Check} title="Tidak ada yang menunggu" /> : (
          <div className="space-y-2">
            {pending.map((w) => (
              <Card key={w.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`waste-req-${w.id}`}>
                <div>
                  <div className="font-medium text-slate-800">{w.item_name} × {num(w.qty)} {w.unit}</div>
                  <div className="text-xs text-slate-400">{w.reason} · rugi ~{rupiah(w.estimated_loss)} · oleh {w.created_by_name} · {fmtDateTime(w.created_at)}</div>
                  {w.notes && <div className="text-xs text-slate-500 mt-0.5">{w.notes}</div>}
                </div>
                {canApprove && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => decide(w.id, "approve")}><Check size={15} /> Setujui</Button>
                    <Button size="sm" variant="danger" onClick={() => decide(w.id, "reject")}><X size={15} /> Tolak</Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )
      ) : (
        !history.length ? <EmptyState icon={Trash2} title="Belum ada riwayat waste" /> : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="py-3 px-4 font-medium">Item</th><th className="py-3 px-4 font-medium">Alasan</th>
                <th className="py-3 px-4 font-medium text-right">Qty</th><th className="py-3 px-4 font-medium text-right">Rugi</th><th className="py-3 px-4 font-medium">Waktu</th>
              </tr></thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-800">{r.item_name}</td>
                    <td className="py-3 px-4"><Badge className="bg-amber-100 text-amber-700">{r.reason_label}</Badge></td>
                    <td className="py-3 px-4 text-right">{num(r.qty)} {r.unit}</td>
                    <td className="py-3 px-4 text-right text-rose-600">{rupiah(r.cost_value)}</td>
                    <td className="py-3 px-4 text-slate-400 text-xs">{fmtDateTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      )}

      {creating && <WasteCreate branch={branch} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </>
  );
}

function WasteCreate({ branch, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ item_id: "", qty: 1, reason: "layu", notes: "", photo_file_id: null });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/materials", branch ? { branch_id: branch.id } : {}).then(setItems); }, [branch]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!branch) return toast.error("Pilih cabang dulu");
    if (!form.item_id) return toast.error("Pilih item");
    if (!form.photo_file_id) return toast.error("Foto bukti wajib");
    setBusy(true);
    try {
      await api.post("/inventory/waste", { branch_id: branch.id, item_id: form.item_id, qty: Number(form.qty), reason: form.reason, notes: form.notes, photo_file_id: form.photo_file_id });
      toast.success("Laporan waste dikirim (menunggu approval)"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Lapor Bunga Rusak" maxWidth="max-w-md">
      <div className="space-y-3">
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Item</span>
          <select value={form.item_id} onChange={(e) => set("item_id", e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="wf-item">
            <option value="">Pilih item...</option>
            {items.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Jumlah</span><input type="number" value={form.qty} onChange={(e) => set("qty", e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" /></label>
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Alasan</span>
            <select value={form.reason} onChange={(e) => set("reason", e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300">{REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </label>
        </div>
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Catatan</span><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl border border-slate-300" /></label>
        <PhotoUpload value={form.photo_file_id} onChange={(id) => set("photo_file_id", id)} label="Foto Bukti (wajib)" testid="wf-photo" />
        <Button className="w-full" onClick={save} disabled={busy} data-testid="wf-save">{busy ? "Mengirim..." : "Kirim Laporan"}</Button>
      </div>
    </Modal>
  );
}
